/* eslint-disable react/no-array-index-key */
import classNames from 'classnames';
import moment from 'moment';
import React, { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import TimelineEventComponent from 'layout/atoms/TimelineEvent';
import styles from './SimpleTimeline.module.scss';
const getEmptyItemProps = () => ({});
const EMPTY_EVENTS = [];
const ROW_PADDING = 4;
const LANE_GAP = 4;
const EVENT_GAP = 4;
// Stacks overlapping events into separate lanes (one under another) based on their rendered width
function TimelineRowEvents({ events }) {
    const itemRefs = useRef([]);
    const [layout, setLayout] = useState({ lanes: [], laneHeight: 0, laneCount: 0 });
    useLayoutEffect(() => {
        itemRefs.current.length = events.length;
        const computeLayout = () => {
            const sizes = events.map((_, idx) => {
                var _a, _b, _c, _d;
                return ({
                    width: (_b = (_a = itemRefs.current[idx]) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 0,
                    height: (_d = (_c = itemRefs.current[idx]) === null || _c === void 0 ? void 0 : _c.offsetHeight) !== null && _d !== void 0 ? _d : 0,
                });
            });
            const order = events
                .map((_, idx) => idx)
                .sort((a, b) => events[a].left - events[b].left || a - b);
            const laneEnds = [];
            const lanes = [];
            order.forEach((idx) => {
                const { left } = events[idx];
                let lane = laneEnds.findIndex((end) => end + EVENT_GAP <= left);
                if (lane === -1) {
                    lane = laneEnds.length;
                }
                laneEnds[lane] = left + sizes[idx].width;
                lanes[idx] = lane;
            });
            const next = {
                lanes,
                laneHeight: Math.max(0, ...sizes.map((size) => size.height)),
                laneCount: laneEnds.length,
            };
            setLayout((prev) => prev.laneHeight === next.laneHeight &&
                prev.laneCount === next.laneCount &&
                prev.lanes.length === next.lanes.length &&
                prev.lanes.every((lane, idx) => lane === next.lanes[idx])
                ? prev
                : next);
        };
        computeLayout();
        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(computeLayout);
        itemRefs.current.forEach((item) => item && observer.observe(item));
        return () => observer.disconnect();
    }, [events]);
    const stackHeight = layout.laneCount * layout.laneHeight + Math.max(0, layout.laneCount - 1) * LANE_GAP;
    return (React.createElement("div", { className: styles.eventsLayer, style: { minHeight: stackHeight + ROW_PADDING * 2 } },
        React.createElement("div", { className: styles.eventsStack, style: { top: `calc(50% - ${stackHeight / 2}px)`, height: stackHeight } }, events.map(({ event, left }, idx) => {
            var _a;
            return (React.createElement("div", { key: event.id, ref: (el) => {
                    itemRefs.current[idx] = el;
                }, className: styles.eventItem, style: { left, top: ((_a = layout.lanes[idx]) !== null && _a !== void 0 ? _a : 0) * (layout.laneHeight + LANE_GAP) } },
                React.createElement(TimelineEventComponent, { item: event, getItemProps: getEmptyItemProps })));
        }))));
}
function SimpleTimeline({ groups, events, pixelsPerDay = 60, renderGroupLeft, renderHeaderLeft, labelWidth = 200, timelinePeriod = 'week', scrollToToday = true, }) {
    const timelineOuterRef = useRef(null);
    const dateRange = useMemo(() => {
        if (events.length === 0) {
            const now = moment();
            return {
                start: now.clone().startOf('week').subtract(4, 'weeks'),
                end: now.clone().endOf('week').add(4, 'weeks'),
            };
        }
        const dates = events.map((e) => ({
            start: moment(e.start_time),
            end: moment(e.end_time),
        }));
        let minDate = dates[0].start.clone();
        let maxDate = dates[0].end.clone();
        dates.forEach(({ start, end }) => {
            if (start.isBefore(minDate))
                minDate = start.clone();
            if (end.isAfter(maxDate))
                maxDate = end.clone();
        });
        const today = moment();
        if (today.isAfter(maxDate))
            maxDate = today.clone();
        minDate = minDate.clone().startOf('week').subtract(4, 'weeks');
        maxDate = maxDate.clone().endOf('week').add(4, 'weeks');
        return { start: minDate, end: maxDate };
    }, [events]);
    const days = useMemo(() => {
        const daysList = [];
        const current = dateRange.start.clone();
        while (current.isBefore(dateRange.end) || current.isSame(dateRange.end, 'day')) {
            daysList.push(current.clone());
            current.add(1, 'day');
        }
        return daysList;
    }, [dateRange]);
    const monthGroups = useMemo(() => {
        const groups = [];
        let currentMonthKey = null;
        let monthStartIdx = 0;
        days.forEach((day, idx) => {
            const monthKey = day.format('YYYY-MM');
            if (monthKey !== currentMonthKey) {
                if (currentMonthKey !== null) {
                    const lastGroup = groups[groups.length - 1];
                    lastGroup.endIdx = idx - 1;
                    lastGroup.span = idx - monthStartIdx;
                }
                currentMonthKey = monthKey;
                monthStartIdx = idx;
                groups.push({
                    month: day.format('MMMM'), // Full month name
                    year: day.format('YYYY'),
                    startIdx: idx,
                    endIdx: idx,
                    span: 1,
                });
            }
            else {
                const lastGroup = groups[groups.length - 1];
                lastGroup.endIdx = idx;
                lastGroup.span = idx - lastGroup.startIdx + 1;
            }
        });
        return groups;
    }, [days]);
    const weekGroups = useMemo(() => {
        const groups = [];
        let currentWeekKey = null;
        let weekStartIdx = 0;
        days.forEach((day, idx) => {
            const monday = day.clone().startOf('week'); // moment.js uses Monday as week start by default with locale
            const weekKey = monday.format('YYYY-MM-DD');
            if (weekKey !== currentWeekKey) {
                if (currentWeekKey !== null) {
                    const lastGroup = groups[groups.length - 1];
                    lastGroup.endIdx = idx - 1;
                    lastGroup.span = idx - weekStartIdx;
                }
                currentWeekKey = weekKey;
                weekStartIdx = idx;
                groups.push({
                    weekLabel: monday.format('DD'), // Day of month for Monday
                    startIdx: idx,
                    endIdx: idx,
                    span: 1,
                });
            }
            else {
                const lastGroup = groups[groups.length - 1];
                lastGroup.endIdx = idx;
                lastGroup.span = idx - lastGroup.startIdx + 1;
            }
        });
        return groups;
    }, [days]);
    // Group events by groupId and position them at the day they start
    const eventsByGroup = useMemo(() => {
        const grouped = {};
        const firstDay = days[0];
        events.forEach((event) => {
            const dayIdx = moment(event.start_time).startOf('day').diff(firstDay, 'days');
            if (dayIdx < 0 || dayIdx >= days.length) {
                return;
            }
            const key = String(event.groupId);
            grouped[key] = grouped[key] || [];
            grouped[key].push({ event, left: dayIdx * pixelsPerDay + ROW_PADDING });
        });
        return grouped;
    }, [days, events, pixelsPerDay]);
    // Every other column (day / week / month, depending on the period) gets a striped background
    const stripedDays = useMemo(() => {
        if (timelinePeriod === 'day') {
            return days.map((_, idx) => idx % 2 === 1);
        }
        const striped = [];
        const periodGroups = timelinePeriod === 'month' ? monthGroups : weekGroups;
        periodGroups.forEach((group, groupIdx) => {
            for (let idx = group.startIdx; idx <= group.endIdx; idx += 1) {
                striped[idx] = groupIdx % 2 === 1;
            }
        });
        return striped;
    }, [days, monthGroups, timelinePeriod, weekGroups]);
    // Scroll to today
    useEffect(() => {
        if (!scrollToToday || !timelineOuterRef.current)
            return;
        const today = moment().startOf('day');
        const todayIdx = days.findIndex((day) => day.isSame(today, 'day'));
        if (todayIdx !== -1) {
            const scrollPosition = labelWidth + todayIdx * pixelsPerDay;
            // Scroll with a small offset to center it better
            timelineOuterRef.current.scrollLeft = scrollPosition - 500;
        }
    }, [days, labelWidth, pixelsPerDay, scrollToToday]);
    return (React.createElement("div", { className: styles.timelineOuter, ref: timelineOuterRef },
        React.createElement("div", { className: styles.timelineWrapper },
            React.createElement("div", { className: styles.monthsHeader },
                React.createElement("div", { className: styles.labelHeader, style: { width: labelWidth, minWidth: labelWidth } }),
                React.createElement("div", { className: styles.monthsRow }, monthGroups.map((group, idx) => (React.createElement("div", { key: idx, className: styles.monthCell, style: {
                        width: `${group.span * pixelsPerDay}px`,
                        minWidth: `${group.span * pixelsPerDay}px`,
                    } }, group.month))))),
            timelinePeriod !== 'day' && (React.createElement("div", { className: styles.weeksHeader },
                React.createElement("div", { className: styles.labelHeader, style: { width: labelWidth, minWidth: labelWidth } }, renderHeaderLeft && (React.createElement("div", { className: styles.columnsHeader },
                    React.createElement("div", { className: styles.leftColumnsHeader, style: { width: labelWidth, minWidth: labelWidth } }, renderHeaderLeft())))),
                React.createElement("div", { className: styles.weeksRow }, weekGroups.map((group, idx) => (React.createElement("div", { key: idx, className: styles.weekCell, style: {
                        width: `${group.span * pixelsPerDay}px`,
                        minWidth: `${group.span * pixelsPerDay}px`,
                    } }, group.weekLabel)))))),
            timelinePeriod === 'day' && (React.createElement("div", { className: styles.header },
                React.createElement("div", { className: styles.labelHeader, style: { width: labelWidth, minWidth: labelWidth } }, renderHeaderLeft && (React.createElement("div", { className: styles.columnsHeader },
                    React.createElement("div", { className: styles.leftColumnsHeader, style: { width: labelWidth, minWidth: labelWidth } }, renderHeaderLeft())))),
                React.createElement("div", { className: styles.daysHeader }, days.map((day, idx) => {
                    const dayOfWeek = day.day();
                    const isWeekend = dayOfWeek === 6 || dayOfWeek === 0; // Saturday or Sunday
                    return (React.createElement("div", { key: idx, className: `${styles.dayHeader} ${isWeekend ? styles.weekend : ''}`, style: { width: `${pixelsPerDay}px`, minWidth: `${pixelsPerDay}px` } },
                        React.createElement("div", { className: styles.dayNumber }, day.format('DD')),
                        React.createElement("div", { className: styles.dayName }, day.format('ddd'))));
                })))),
            React.createElement("div", { className: styles.rowsContainer }, groups.map((group) => (React.createElement("div", { key: group.id, className: styles.row },
                React.createElement("div", { className: styles.labelCell, style: { width: labelWidth, minWidth: labelWidth } }, renderGroupLeft ? renderGroupLeft(group) : React.createElement("span", null, group.title)),
                React.createElement("div", { className: styles.daysRow },
                    days.map((_, dayIdx) => (React.createElement("div", { key: dayIdx, className: classNames(styles.dayCell, {
                            [styles.striped]: stripedDays[dayIdx],
                        }), style: { width: `${pixelsPerDay}px`, minWidth: `${pixelsPerDay}px` } }))),
                    React.createElement(TimelineRowEvents, { events: eventsByGroup[String(group.id)] || EMPTY_EVENTS })))))))));
}
export default memo(SimpleTimeline);
